/**
 * BNPL onboarding copy (ERS-5, restructured ERS-78)
 *
 * All German strings for the three BNPL screens (financing 07, data 08, payment 09) + detail
 * drawer + reject screen live here, insurer name and amounts interpolated, so a copy change
 * (ERS-22 sign-off) or a pricing change (ERS-26) is a one-file diff. Mirrors `insurance/copy.ts`'s
 * shape.
 *
 * Transcribed from the Figma frames; subject to ERS-22 sign-off.
 */

import { formatEuro, formatRefundPercent } from './copyUtils';
import type { InsuranceCoverage } from './metaBuilders';

// --- Financing screen (07) ---------------------------------------------------------

export const BNPL_FINANCING_HEADLINE = 'Erstattung bestätigt. Deine Kurskosten werden vollständig übernommen 🎉';

/** Intro under the headline. Branches on the amount (`isFullCoverage`), never on `refundRate`. */
export function buildBnplFinancingIntro(insuranceName: string, coverage: InsuranceCoverage): string {
  return coverage.isFullCoverage
    ? `Die ${insuranceName} übernimmt die volle Kursgebühr für deinen Gesundheitskurs.`
    : `Die ${insuranceName} und Physiofit übernehmen die volle Kursgebühr für deinen Gesundheitskurs.`;
}

export function buildBnplPriceCardCopy(coverage: InsuranceCoverage) {
  return {
    originalPrice: formatEuro(coverage.courseFeeEur, 'plain', true),
    price: formatEuro(0, 'plain', true),
    suffix: 'für dich',
    note: 'Ganz unverbindlich trainieren. Du gehst kein Abo und keine Verpflichtung ein.',
  };
}

export const BNPL_COST_TABLE_LABELS = {
  /** The week count is the required-unit count (`REQUIRED_COURSE_UNITS`), not `Course.duration`, which counts the optional weeks too. */
  courseFee: (weeks: number) => `Kursgebühr (${weeks} Wochen)`,
  insurerRefund: 'Erstattung deiner Kasse',
  physiofitSubsidy: 'Zuschuss durch Physiofit',
  yourPrice: 'Dein Preis',
  totalForYou: 'Gesamt für dich',
  dueToday: 'Heute fällig',
  a11ySummary: 'Kostenübersicht nach Kursabschluss',
} as const;

export const BNPL_FINANCING_BENEFITS_HEADING = 'Deine Vorteile mit Physiofit';

/**
 * The two fixed benefit rows. The submission row names Physiofit as the submitter unless the insurer
 * requires the patient to hand in the course (`isPatientSubmittingInsurer`); the frames show only the
 * Physiofit wording, the patient wording mirrors the TK drawer frame.
 *
 * @param {boolean} patientSubmits True when the patient hands in the course themselves.
 * @return {Array} Ordered benefit rows.
 */
export function buildBnplFinancingBenefits(patientSubmits: boolean): string[] {
  return [
    'Starte & trainiere unverbindlich',
    patientSubmits ? 'Wir begleiten dich bei der Einreichung' : 'Wir übernehmen die Einreichung',
  ];
}

/**
 * Bonus benefit title (third benefit row, only rendered when the insurer runs a bonus programme).
 * Prints the amount the insurer names in the catalog (`bonus.valueEuro`, e.g. "Zusätzlich 10 € Prämie
 * von der TK" as the frames show) and falls back to the amount-less title when it names none.
 *
 * @param {string} insuranceShortName Short display name of the insurer.
 * @param {number | undefined} bonusEur Bonus amount in EUR when the catalog carries one.
 * @return {string} Benefit title.
 */
export function buildBnplBonusBenefit(insuranceShortName: string, bonusEur?: number): string {
  if (bonusEur && bonusEur > 0) {
    return `Zusätzlich ${formatEuro(bonusEur, 'plain', true)} Prämie von der ${insuranceShortName}`;
  }
  return `Zusätzlich eine Prämie von der ${insuranceShortName}`;
}

export const BNPL_FINANCING_CTA = 'Weiter zur Anmeldung';

/**
 * Legal footnote under the support card: full certified course name and ZPP course id.
 *
 * @param {string} designation Full certified course name (`zppName`).
 * @param {string} zppId ZPP certification id.
 * @return {string} Footnote, or an empty string when neither value is available.
 */
export function buildBnplCourseLegalFootnote(designation: string, zppId: string): string {
  const name = designation.trim();
  const id = zppId.trim();
  if (!name && !id) {
    return '';
  }
  const nameSentence = name ? `Der vollständige Kursname ist: „${name}“.` : '';
  const idSentence = id ? `Der Kurs ist zertifiziert unter der Kurs-Id: „${id}“.` : '';
  return [nameSentence, idSentence].filter(Boolean).join(' ');
}

// --- Data screen (08) ---------------------------------------------------------------

/** Figma writes "Fasst geschafft!"; the doubled s is a typo we do not reproduce. */
export const BNPL_DATA_HEADLINE = 'Fast geschafft!';

/** Figma writes "brauchen wir noch noch" — the duplicated word is a typo we do not reproduce. */
export const BNPL_CHECKOUT_SUBTITLE =
  'Für die Abrechnung mit deiner Krankenkasse brauchen wir noch ein paar unvermeidbare Formalien.';

export const BNPL_CHECKOUT_FIELD_LABELS = {
  firstName: 'Vorname',
  lastName: 'Nachname',
  street: 'Straße und Hausnummer',
  zip: 'PLZ',
  city: 'Ort',
  birthday: 'Geburtsdatum',
  phone: 'Handynummer',
  email: 'E-Mail-Adresse',
} as const;

export const BNPL_CHECKOUT_PHONE_HINT = 'Mit dieser Nummer loggst du dich später in der App ein.';

export const BNPL_DATA_FIELDS_ERROR_NOTICE = 'Bitte fülle alle Felder aus, um fortzufahren.';

export const BNPL_BIRTHDAY_INVALID_ERROR = 'Bitte prüfe dein Geburtsdatum, besonders das Jahr.';

/** Field error naming the characters the provider would reject. */
export function buildBnplDisallowedCharactersError(characters: string[]): string {
  return characters.length === 1
    ? `Bitte entferne dieses Zeichen: ${characters[0]}`
    : `Bitte entferne diese Zeichen: ${characters.join(' ')}`;
}

export const BNPL_DATA_CTA = 'Weiter zur Bestellübersicht';

// --- Payment screen (09) -------------------------------------------------------------

export const BNPL_PAYMENT_HERO_TITLE = 'Starte deinen Kurs';
export const BNPL_PAYMENT_SUBTITLE =
  'Letzter Schritt: Wähle deinen bevorzugten Prozess der Erstattung und schon kannst du loslegen!';

export const BNPL_PAYMENT_METHOD_COPY = {
  legend: 'Erstattungsprozess',
  hint: 'Wähle deinen Erstattungsprozess',
  bnplSectionLabel: 'Ohne Vorleistung',
  recommendedBadge: 'Empfohlen',
  bnplTitle: 'Abrechnung durch Physiofit',
  bnplDescription: 'Wir übernehmen die Erstattung für dich. Dein Konto wird nicht belastet.',
  invoiceSectionLabel: 'Selbst abrechnen',
  invoiceTitle: '📄  Kauf auf eigene Rechnung',
  invoiceDescription:
    'Du übernimmst die Erstattung selbst. Schließt du den Kurs erfolgreich ab, zahlst du selbst und reichst die Unterlagen selbst ein.',
} as const;

export const BNPL_PAYMENT_DETAIL_TRIGGER_LABEL = "So funktioniert's";

/**
 * Inline "Ablauf" for the inline detail style. The second step names Physiofit as the submitter unless
 * the patient hands in the course themselves (no frame exists for that; the sentence mirrors the TK
 * drawer frame); the third step adds the subsidy sentence on partial coverage.
 *
 * @param {string} insuranceShortName Short display name of the insurer.
 * @param {Object} coverage Coverage facts.
 * @param {boolean} patientSubmits True when the patient hands in the course themselves.
 * @return {Array} Ordered steps for `CompactTimeline`.
 */
export function buildBnplPaymentTimeline(
  insuranceShortName: string,
  coverage: InsuranceCoverage,
  patientSubmits: boolean,
) {
  const subsidySentence = coverage.isFullCoverage ? '' : ' Die Differenz übernimmt Physiofit.';
  const submissionSentence = patientSubmits
    ? `Wir begleiten dich bei der Kurseinreichung, du musst lediglich die Übermittlung an die ${insuranceShortName} übernehmen.`
    : 'Wir reichen alles bei deiner Kasse ein.';
  return [
    { title: 'Heute', body: 'Du startest dein Programm. Es wird nichts abgebucht und du kannst jederzeit aufhören.' },
    {
      title: 'Nach Kursende',
      body: `Du bestätigst den Abschluss in der App. ${submissionSentence} Damit die ${insuranceShortName} dir die Kursgebühr nach Kursabschluss erstatten darf, geht unser Zahlungspartner Cashpresso für dich in Vorleistung.`,
    },
    {
      title: 'Wenige Tage später',
      body: `Deine Kasse erstattet direkt auf dein Konto. Diesen Betrag leitest du an Cashpresso weiter, wir erinnern dich rechtzeitig.${subsidySentence}`,
    },
  ];
}

export const BNPL_PAYMENT_INFO_CARD =
  'Du kannst das Training jederzeit abbrechen. Beendest du den Kurs nicht, findet keine Abrechnung mit der Kasse statt und du hast einfach kostenlos trainiert.';

/**
 * Design copy (Figma `26620:19180`), which carries no Haftungsausschluss link — so the liability
 * drawer is no longer reachable from this screen. The substance the patient confirms is unchanged.
 */
export const BNPL_CHECKOUT_LIABILITY_CHECKBOX_TEXT =
  'Ich bestätige, dass ich an keinen behandlungsbedürftigen Erkrankungen des Bewegungsapparats leide.';

export const BNPL_CHECKOUT_PROVIDER_PRIVACY_CHECKBOX_TEXT =
  'Ich habe die Datenschutzbestimmung der RBI zur Kenntnis genommen.';

export const BNPL_PAYMENT_CTA = 'Jetzt Kaufen';
export const buildBnplPaymentCtaHint = (amount: string) => `(du zahlst: ${amount})`;

/**
 * Submit progress copy (ERS-61 D3). Ten fragments at 1000 ms = 10 s, then the last one holds.
 *
 * The cadence started at 500 ms and was doubled once the real wait turned out to be longer than the
 * five seconds it was paced for. The fragments stay short anyway — they were written for a tick that
 * does not give a full sentence time to land, and a short line is no worse at a slower tick.
 * The order mirrors the real server sequence in `runCoursePaymentCheckout`
 * (init → authorization init → get authorization → accept documents → perform authorization), so
 * the copy stays honest if the timing shifts.
 *
 * Every entry has to fit `OnboardingBusyOverlay`'s fixed one-line width, which is why the two
 * longest lost a word ("Deine Angaben…" → "Angaben…", "…dokumentiert" → "…gesichert"). The budget is
 * 248 px at 15 px Ubuntu; the longest entry now measures 237 px. Check a new or edited line against
 * that before adding it — anything over the budget gets an ellipsis rather than a second line.
 *
 * Naming the Bonitätsprüfung is intentional and consistent: the financing screen two steps earlier
 * already tells the patient "prüft die Bank beim Kursstart einmalig deine Bonität. Das dauert nur
 * wenige Sekunden." This confirms that promise instead of springing a surprise at the moment of
 * commitment. The patient-facing partner name is Cashpresso; RBI appears only in the legal checkbox.
 */
export const BNPL_CHECKOUT_SUBMIT_PROGRESS_MESSAGES = ['Angaben werden gespeichert…'] as const;

/** Stable accessible name for the submit overlay — the rotating copy is not announced. */
export const BNPL_CHECKOUT_SUBMIT_PROGRESS_ARIA_LABEL = 'Dein Kursstart wird eingerichtet.';

export const BNPL_CHECKOUT_LEGAL_DOCUMENT_UNAVAILABLE_NOTICE =
  'Die Datenschutzbestimmung unseres Partners konnte gerade nicht geladen werden. Bitte versuche es in Kürze erneut.';

export const BNPL_CHECKOUT_CONSENT_OUTDATED_NOTICE =
  'Die Datenschutzbestimmung unseres Partners wurde aktualisiert. Bitte lies sie dir erneut durch und bestätige sie, um fortzufahren.';

// --- Detail drawer ("Ablauf nach deinem Kursabschluss im Detail") --------------

export const BNPL_DRAWER_TITLE = 'Ablauf nach deinem Kursabschluss im Detail';
export const BNPL_DRAWER_FAQ_HEADING = 'Die häufigsten Fragen';
export const BNPL_DRAWER_CLOSE_LABEL = 'Schließen';

export type BnplDrawerStep = {
  /** Stable key for React, not shown. */
  key: string;
  title: string;
  body: string;
  /** Set on the bonus step: gift marker, success-green circle. */
  isBonus?: boolean;
};

/** Used when the insurer carries no `bonus.description` — see O2. */
export const BNPL_DRAWER_BONUS_FALLBACK_BODY =
  'Du erhältst von uns deine Teilnahmebescheinigung und kannst damit deinen Bonus bei deiner Krankenkasse beantragen.';

/**
 * The drawer's timeline for one insurer. Steps 1–3 are insurer-name-only; steps 4 and 5 branch on
 * coverage (Figma `*-Full-Refund-Details` vs `*-Partial-Refund-Details`); the bonus step exists only
 * for `hasBonus` (the `*-Bonus` frames). Step 3 switches to the patient-submits wording of the TK
 * frame (`Partial-Refund-Details-Bonus`) for insurers that make the patient hand in the course.
 *
 * @param {Object} args Insurer short name, coverage facts, bonus flag, the insurer's own bonus text and
 *   whether the patient submits the course themselves.
 * @return {Array} Ordered steps for `ListTimeline`.
 */
export function buildBnplDrawerSteps(args: {
  insuranceShortName: string;
  coverage: InsuranceCoverage;
  hasBonus: boolean;
  bonusDescription?: string;
  patientSubmits: boolean;
}): BnplDrawerStep[] {
  const { insuranceShortName: name, coverage, hasBonus, bonusDescription, patientSubmits } = args;
  const refund = formatEuro(coverage.insurerRefundEur, 'plain');
  const subsidy = formatEuro(coverage.physiofitSubsidyEur, 'plain');
  const share = formatRefundPercent(coverage.insurerSharePercent / 100);

  const steps: BnplDrawerStep[] = [
    {
      key: 'start',
      title: 'Du startest deinen Kurs',
      body: `Du schließt die Registrierung ab und startest deinen Kurs in der App. Du kannst den Kurs jederzeit kostenfrei beenden.`,
    },
    {
      key: 'complete',
      title: 'Du schließt den Kurs ab',
      body: `Du bestätigst den erfolgreichen Kursabschluss in der App. Nachfolgend geht unser Partner Cashpresso für dich in Vorleistung, um die Erstattung mit der Kasse zu starten – diese verlangt dies leider.`,
    },
    patientSubmits
      ? {
          key: 'submit',
          title: `Wir unterstützen bei der Kurseinreichung bei der ${name}`,
          body: `Wir begleiten dich bei der Kurseinreichung, du musst lediglich die Übermittlung an die ${name} übernehmen.`,
        }
      : {
          key: 'submit',
          title: `Wir reichen den Kurs bei der ${name} ein`,
          body: `Wir nehmen dir die gesamte Bürokratie ab und reichen den Kurs direkt bei der ${name} ein.`,
        },
    {
      key: 'refund',
      title: `Die ${name} erstattet dir ${refund}`,
      body: coverage.isFullCoverage
        ? `Die ${name} überweist dir die Erstattung direkt auf dein Konto.`
        : `Die ${name} überweist dir ${refund} (${share} der Kursgebühr) als Erstattung direkt auf dein Konto. Die restlichen ${subsidy} übernehmen wir als Förderung für dich.`,
    },
    {
      key: 'forward',
      title: 'Du leitest die Erstattung weiter',
      body: coverage.isFullCoverage
        ? `Die Erstattung der ${name} leitest du an Cashpresso weiter und begleichst damit die offene Rechnung. Die Zahlungsinformationen bekommst du per E-Mail.`
        : `Die Erstattung der ${name} leitest du an Cashpresso weiter und begleichst damit zusammen mit unserer Förderung die offene Rechnung. Die Zahlungsinformationen bekommst du per E-Mail.`,
    },
  ];

  if (hasBonus) {
    steps.push({
      key: 'bonus',
      isBonus: true,
      title: `Hol dir deinen ${name} Bonus`,
      // The insurer's own sentence stays authoritative here; the frame's templated line prints 15 €
      // for the TK, whose catalog amount is 10 €.
      body: bonusDescription?.trim() || BNPL_DRAWER_BONUS_FALLBACK_BODY,
    });
  }

  return steps;
}

export type BnplDrawerFaqItem = { key: string; question: string; answer: string };

/**
 * The drawer's five FAQs in the design's order (Figma `Abrechnung FAQ`). Only the cost answer
 * branches on coverage; the other four are identical across all four frames.
 *
 * @param {string} insuranceShortName Short display name of the insurer.
 * @param {Object} coverage Coverage facts.
 * @return {Array} Ordered FAQ items.
 */
export function buildBnplDrawerFaq(insuranceShortName: string, coverage: InsuranceCoverage): BnplDrawerFaqItem[] {
  const refund = formatEuro(coverage.insurerRefundEur, 'plain');
  const subsidy = formatEuro(coverage.physiofitSubsidyEur, 'plain');

  return [
    {
      key: 'cost',
      question: 'Was kostet mich der Kurs?',
      answer: coverage.isFullCoverage
        ? `Nichts, die ${insuranceShortName} übernimmt die gesamte Kursgebühr.`
        : `Nichts, nach deinem Kursabschluss übernehmen die ${insuranceShortName} (${refund}) und wir (${subsidy}) gemeinsam die gesamte Kursgebühr für dich.`,
    },
    {
      key: 'cancel',
      question: 'Was passiert, wenn mir der Kurs nicht gefällt oder ich aufhören möchte?',
      answer:
        'Überhaupt kein Problem, du kannst jederzeit aufhören. In diesem Fall erhältst du keine Rechnung und es entstehen dir keine Kosten.',
    },
    {
      key: 'why-invoice',
      question: 'Wieso bekomme ich eine Rechnung, wenn der Kurs für mich kostenlos ist?',
      answer:
        'Das ist eine rechtliche Vorgabe: Deine Krankenkasse darf die Kursgebühr nur erstatten, wenn der Anbieter (wir) diese erhalten hat. Damit du nicht in Vorleistung gehen musst, übernimmt dies unser Partner Cashpresso für dich.',
    },
    {
      key: 'no-refund',
      question: 'Was passiert, wenn meine Kasse die Kursgebühr nicht erstattet?',
      answer:
        'In diesem Fall greift unsere „Kostenfrei-Garantie“, schick uns einfach das Schreiben deiner Kasse und wir übernehmen die Rechnung. Es entstehen dir keine Kosten.',
    },
    {
      key: 'credit-check',
      question: 'Für was ist die Bonitätsprüfung?',
      answer:
        'Weil Cashpresso für dich in Vorleistung geht, prüft die Bank beim Kursstart einmalig deine Bonität. Das dauert nur wenige Sekunden.',
    },
  ];
}

// --- Rejected --------------------------------------------------------------------

/**
 * Placeholder reject copy, ported from v1 (`dev/legacyBnplV1/config/onboardingDisplayConfig.ts`) per
 * the 2026-08-20 decision — replaced once ERS-12 (reject-path product decision) and ERS-22 (copy
 * sign-off) land. Kept here so that swap is a one-file diff.
 */
export const BNPL_REJECTED_COPY = {
  title: 'Hmm, das hat leider nicht geklappt.',
  subtitle:
    'Unser Abwicklungspartner hat dich nicht freigegeben. Du kannst den Kurs trotzdem nutzen — allerdings ohne unsere Einreichungsunterstützung und ohne Bonus.',
  disclaimer:
    'Gründe können z.B. eine Schufa-Auskunft, Adressunklarheiten oder fehlende Daten sein. Wir erhalten von unserem Partner keine Details.',
  primaryCta: 'Trotzdem starten',
} as const;

// --- Shared ---------------------------------------------------------------------

export const BNPL_CHECKOUT_GENERIC_ERROR_NOTICE = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';

/** Hedged on purpose: the provider's `invalid-argument` also covers 4xx causes other than a special character. */
export const BNPL_CHECKOUT_INVALID_DATA_ERROR_NOTICE =
  'Wir konnten deine Angaben nicht übermitteln. Möglicherweise enthalten Name oder Adresse ein Sonderzeichen, das wir nicht verarbeiten können. Bitte geh zurück, prüfe deine Angaben und versuche es erneut.';
